if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();
        this.form = this.querySelector('form');
        if (!this.form) return;

        if (this.variantIdInput) this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));

        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('button[name="add"]') || this.querySelector('[type="submit"]');
        this.codButton = this.querySelector('[data-bhakti-cod-order]');
        this.purchaseButtons = [this.submitButton, this.codButton].filter(Boolean);
        this.hideErrors = this.dataset.hideErrors === 'true';
        this.requestInFlight = false;

        if (this.cart && this.submitButton) this.submitButton.setAttribute('aria-haspopup', 'dialog');
      }

      get targetVariants() {
        return new Set(['48556085444849', '48556085575921', '48566646931697', '67114116088049']);
      }

      isTargetVariant(variantId) {
        return this.targetVariants.has(String(variantId || ''));
      }

      getSelectedQuantity(variantId) {
        if (this.isTargetVariant(variantId)) return 1;
        const quantityControl = this.form?.elements?.namedItem('quantity');
        const quantityInput = quantityControl instanceof RadioNodeList ? quantityControl[0] : quantityControl;
        return Math.max(1, parseInt(quantityInput?.value, 10) || 1);
      }

      getItems(variantId, quantity) {
        return [{ id: Number(variantId), quantity }];
      }

      cartContainsPaidVariant(variantId) {
        if (!this.cart) return false;
        return Array.from(this.cart.querySelectorAll('[data-quantity-variant-id]')).some((input) => {
          return String(input.dataset.quantityVariantId || '') === String(variantId)
            && Number(input.value || input.getAttribute('value') || 0) > 0;
        });
      }

      getSectionsParam() {
        if (!this.cart?.getSectionsToRender) return '';
        const ids = this.cart.getSectionsToRender()
          .map((section) => section.section || section.id)
          .filter(Boolean);
        return [...new Set(ids)].join(',');
      }

      setBusy(activeButton, busy) {
        this.purchaseButtons.forEach((button) => {
          if (busy) button.setAttribute('aria-disabled', 'true');
          else if (!button.disabled) button.removeAttribute('aria-disabled');
        });

        if (!activeButton) return;
        activeButton.classList.toggle('loading', busy);
        activeButton.querySelector('.loading__spinner')?.classList.toggle('hidden', !busy);
      }

      openCart(activeButton) {
        if (!this.cart) {
          window.location.assign(window.routes?.cart_url || '/cart');
          return;
        }
        this.cart.classList?.remove('is-empty');
        this.cart.setActiveElement?.(document.activeElement || activeButton);
        this.cart.open?.(activeButton);
        window.BhakthiCleanCart?.applyCopy?.();
      }

      async addToCart(requestBody) {
        const controller = new AbortController();
        const timeout = window.setTimeout(() => controller.abort(), 12000);

        try {
          const response = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
              'X-Requested-With': 'XMLHttpRequest'
            },
            credentials: 'same-origin',
            body: JSON.stringify(requestBody),
            signal: controller.signal
          });

          const payload = await response.json();
          if (!response.ok || payload?.status || payload?.errors) {
            const message = payload?.description || payload?.message || 'Unable to add this item.';
            throw new Error(message);
          }
          return payload;
        } finally {
          window.clearTimeout(timeout);
        }
      }

      async onSubmitHandler(event) {
        event.preventDefault();

        const activeButton = event.submitter instanceof HTMLElement ? event.submitter : this.submitButton;
        if (
          !activeButton ||
          this.requestInFlight ||
          activeButton.disabled ||
          activeButton.getAttribute('aria-disabled') === 'true'
        ) return;

        const checkoutAfterAdd = activeButton.matches('[data-bhakti-cod-order]');
        const variantId = String(this.variantIdInput?.value || '');
        if (!variantId) {
          this.handleErrorMessage(window.variantStrings?.unavailable || 'This option is unavailable.');
          return;
        }

        const isTarget = this.isTargetVariant(variantId);

        if (isTarget && this.cartContainsPaidVariant(variantId)) {
          this.handleErrorMessage();
          if (checkoutAfterAdd) window.location.assign('/checkout');
          else this.openCart(activeButton);
          return;
        }

        const quantity = this.getSelectedQuantity(variantId);
        const items = this.getItems(variantId, quantity);
        const sections = !checkoutAfterAdd ? this.getSectionsParam() : '';
        const requestBody = { items };
        if (sections) {
          requestBody.sections = sections;
          requestBody.sections_url = window.location.pathname;
        }

        this.requestInFlight = true;
        this.handleErrorMessage();
        this.setBusy(activeButton, true);

        try {
          const payload = await this.addToCart(requestBody);

          if (checkoutAfterAdd) {
            window.location.assign('/checkout');
            return;
          }

          if (!this.cart) {
            window.location.assign(window.routes?.cart_url || '/cart');
            return;
          }

          if (payload?.sections?.['cart-drawer'] && typeof this.cart.renderContents === 'function') {
            this.cart.classList.remove('is-empty');
            this.cart.setActiveElement?.(document.activeElement || activeButton);
            this.cart.renderContents({ ...payload, id: variantId, sections: payload.sections });
            window.BhakthiCleanCart?.applyCopy?.();
          } else {
            this.openCart(activeButton);
            this.cart.querySelector?.('cart-drawer-items')?.onCartUpdate?.();
          }
        } catch (error) {
          console.error('[Bhakthination cart]', error);
          const timedOut = error?.name === 'AbortError';
          this.handleErrorMessage(
            timedOut
              ? 'The connection took too long. Please tap Add to cart again.'
              : (error?.message || 'We could not add this item. Please try again.')
          );
        } finally {
          this.requestInFlight = false;
          this.setBusy(activeButton, false);
          document.querySelector('cart-drawer')?.removeAttribute('data-bhakti-pending');
        }
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;
        this.errorMessageWrapper = this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');
        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);
        if (this.errorMessage) this.errorMessage.textContent = errorMessage || '';
      }

      toggleSubmitButton(disable = true, text) {
        this.purchaseButtons.forEach((button) => {
          button.toggleAttribute('disabled', disable);
          button.toggleAttribute('aria-disabled', disable);
        });
        if (text && this.submitButton) {
          const span = this.submitButton.querySelector('span:not(.sold-out-message)');
          if (span) span.textContent = text;
        }
      }

      get variantIdInput() {
        return this.form?.querySelector('[name="id"]');
      }
    }
  );
}
